import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "sonner";

import { LanguageProvider } from "@/i18n";
import { trackPageview } from "@/lib/analytics";

import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import WhatsAppButton from "@/components/WhatsAppButton";
import Home from "@/pages/Home";
import About from "@/pages/About";
import VerticalPage from "@/pages/VerticalPage";
import Sourcing from "@/pages/Sourcing";
import Partners from "@/pages/Partners";
import Quality from "@/pages/Quality";
import Insights from "@/pages/Insights";
import ArticlePage from "@/pages/ArticlePage";
import Contact from "@/pages/Contact";
import Unsubscribe from "@/pages/Unsubscribe";
import AdminLogin from "@/pages/AdminLogin";
import AdminInbox from "@/pages/AdminInbox";

const Page = ({ children }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
    {children}
  </motion.div>
);

/** GA4 SPA pageview tracking — fires on route change (initial load is sent by gtag config). */
const RouteTracker = () => {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    trackPageview(pathname);
  }, [pathname]);
  return null;
};

const MarketingRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Page><Home /></Page>} />
        <Route path="/about" element={<Page><About /></Page>} />
        <Route path="/fruit-trading" element={<Page><VerticalPage /></Page>} />
        <Route path="/rootstock" element={<Page><VerticalPage /></Page>} />
        <Route path="/mango-export" element={<Page><VerticalPage /></Page>} />
        <Route path="/sourcing" element={<Page><Sourcing /></Page>} />
        <Route path="/partners" element={<Page><Partners /></Page>} />
        <Route path="/quality" element={<Page><Quality /></Page>} />
        <Route path="/insights" element={<Page><Insights /></Page>} />
        <Route path="/insights/:slug" element={<Page><ArticlePage /></Page>} />
        <Route path="/contact" element={<Page><Contact /></Page>} />
        <Route path="/unsubscribe" element={<Page><Unsubscribe /></Page>} />
        <Route path="*" element={<Page><Home /></Page>} />
      </Routes>
    </AnimatePresence>
  );
};

/** Chooses layout: bare admin screens vs. the marketing site with Nav/Footer/WhatsApp. */
const Layout = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");

  if (isAdmin) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminInbox />} />
      </Routes>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Nav />
      <MarketingRoutes />
      <WhatsAppButton variant="floating" />
      <Footer />
    </>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    window.__lenis = lenis;
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <HelmetProvider>
      <LanguageProvider>
        <div className="App grain">
          <Toaster theme="light" position="top-center" richColors />
          <BrowserRouter>
            <RouteTracker />
            <Layout />
          </BrowserRouter>
        </div>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
